/**
 * The URL builder behind `@/lib/routes`. Nothing here knows about this app:
 * it turns a Next.js file-route pattern into something that builds and parses
 * URLs for that pattern, checked against the route types Next generates.
 *
 *   route("/trip/[[...slug]]").href({ slug: [ref] })  // ok
 *   route("/trip/[[...slug]]").href({ id: ref })      // no `id` on this route
 *   route("/trpi/[[...slug]]")                        // no such page
 *
 * Every builder percent-encodes segments and drops undefined query values,
 * which a hand-written template literal does not, and `href()` returns a plain
 * string, so builders drop into `router.push`, `<Link href>` and
 * `<Button href>` unchanged. A param value is one segment: a slash inside it
 * is encoded, so catch-all pieces are passed as separate array elements.
 *
 * The app's own routes live in `@/lib/routes`; import that, not this.
 */

import type { PageRoutes, ParamMap, RewriteRoutes } from "next-typed-routes"

import { env } from "@/lib/env"
import { reportUnexpectedError } from "@/lib/report-unexpected-error"

// ---------- Routes Next.js knows about ----------

/**
 * Every route the app can navigate to: a page file under `src/pages`, or a
 * rewrite source declared in next.config.js. Regenerated by `next typegen`,
 * so a renamed or deleted page turns every stale reference into a type error.
 */
export type AppRoute = PageRoutes | RewriteRoutes

// ---------- Param shapes ----------

type Segment<S extends string> = S extends `[[...${infer N}]]`
  ? { readonly [K in N]?: readonly string[] }
  : S extends `[...${infer N}]`
    ? { readonly [K in N]: readonly string[] }
    : S extends `[${infer N}]`
      ? { readonly [K in N]: string }
      : unknown

type PathParamsRaw<P extends string> = P extends `${infer Head}/${infer Rest}`
  ? Segment<Head> & PathParamsRaw<Rest>
  : Segment<P>

/** Flattens an intersection into a single object type for readable errors. */
type Simplify<T> = { [K in keyof T]: T[K] } & {}

/** Next generates params as mutable; callers pass literals, so freeze them. */
type ReadonlyParams<T> = {
  readonly [K in keyof T]: NonNullable<T[K]> extends readonly (infer E)[]
    ? readonly E[]
    : T[K]
}

/**
 * Params for a pattern, taken from Next's generated map. A pattern it does
 * not know falls back to parsing the pattern text, which is what keeps params
 * typed before the first typegen run.
 */
export type PathParams<P extends string> = P extends keyof ParamMap
  ? Simplify<ReadonlyParams<ParamMap[P]>>
  : Simplify<PathParamsRaw<P>>

type QueryValue = string | number | boolean | readonly string[] | undefined
export type QueryParams = Record<string, QueryValue>

type HasRequiredKeys<T> = {
  [K in keyof T]-?: object extends Pick<T, K> ? never : K
}[keyof T] extends never
  ? false
  : true

type HrefInput<P extends string, Q extends QueryParams> = PathParams<P> & {
  /** Typed object, or an already-built URLSearchParams for pass-through. */
  readonly query?: Q | URLSearchParams
}

/** Makes the argument optional when the route has no required params. */
type HrefArgs<P extends string, Q extends QueryParams> =
  HasRequiredKeys<PathParams<P>> extends true
    ? [input: HrefInput<P, Q>]
    : [input?: HrefInput<P, Q>]

export interface Route<P extends string, Q extends QueryParams> {
  readonly pattern: P
  /** Builds a relative URL, encoding every segment and query value. */
  href(...args: HrefArgs<P, Q>): string
  /**
   * Parses a path (with or without a query string) against the pattern.
   * Returns the path params plus the raw query, or null when it does not match.
   */
  match(
    path: string
  ): (PathParams<P> & { readonly query: Record<string, string> }) | null
  /** True when `path` matches this route, ignoring the query string. */
  test(path: string): boolean
  /**
   * The same route with its query params typed. Purely a type narrowing, and
   * it keeps the pattern written once: `route<P, Q>(p)` would need `P` spelled
   * out again, because TypeScript cannot infer one type argument and take the
   * other.
   */
  query<Q2 extends QueryParams>(): Route<P, Q2>
}

// ---------- Runtime ----------

type ParsedSegment =
  | { readonly kind: "literal"; readonly value: string }
  | {
      readonly kind: "single" | "catchAll" | "optionalCatchAll"
      readonly name: string
    }

function parseSegments(pattern: string): ParsedSegment[] {
  return pattern
    .split("/")
    .filter(Boolean)
    .map((seg): ParsedSegment => {
      const optional = /^\[\[\.\.\.(\w+)\]\]$/.exec(seg)
      if (optional) return { kind: "optionalCatchAll", name: optional[1] }
      const catchAll = /^\[\.\.\.(\w+)\]$/.exec(seg)
      if (catchAll) return { kind: "catchAll", name: catchAll[1] }
      const single = /^\[(\w+)\]$/.exec(seg)
      if (single) return { kind: "single", name: single[1] }
      return { kind: "literal", value: seg }
    })
}

interface CompiledPattern {
  readonly segments: readonly ParsedSegment[]
  readonly matcher: RegExp
}

function buildMatcher(segments: readonly ParsedSegment[]): RegExp {
  return new RegExp(
    "^" +
      segments
        .map((seg) => {
          switch (seg.kind) {
            case "literal":
              return "/" + seg.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
            case "single":
              return `/(?<${seg.name}>[^/]+)`
            case "catchAll":
              return `/(?<${seg.name}>.+)`
            case "optionalCatchAll":
              return `(?:/(?<${seg.name}>.+))?`
          }
        })
        .join("") +
      "/?$"
  )
}

/** Patterns are a closed set, so parse and compile each at most once. */
const compiledPatterns = new Map<string, CompiledPattern>()

function compilePattern(pattern: string): CompiledPattern {
  const cached = compiledPatterns.get(pattern)
  if (cached) return cached
  const segments = parseSegments(pattern)
  const compiled: CompiledPattern = {
    segments,
    matcher: buildMatcher(segments),
  }
  compiledPatterns.set(pattern, compiled)
  return compiled
}

/**
 * A missing param is a call-site bug, so it throws where a developer sees it.
 * In production it degrades to the empty segment a template literal would have
 * produced, a dead link to a 404, and reports instead; a throw here happens
 * mid-render and would take the whole page down through the error boundary.
 */
function missingParam(message: string): string {
  const error = new Error(message)
  if (env("NODE_ENV") !== "production") throw error
  reportUnexpectedError(error, "route-builder")
  return ""
}

/** decodeURIComponent that returns the raw text for a malformed escape. */
function decodeSegment(raw: string): string {
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

function toSearchParams(query: QueryParams): URLSearchParams {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue
    if (Array.isArray(value)) {
      for (const v of value) search.append(key, v)
    } else {
      search.set(key, String(value))
    }
  }
  return search
}

function appendQuery(
  path: string,
  query: QueryParams | URLSearchParams | undefined
): string {
  if (!query) return path
  const qs = (
    query instanceof URLSearchParams ? query : toSearchParams(query)
  ).toString()
  return qs ? `${path}?${qs}` : path
}

const SEGMENT_RANK: Record<ParsedSegment["kind"], number> = {
  literal: 0,
  single: 1,
  catchAll: 2,
  optionalCatchAll: 3,
}

/**
 * Orders patterns the way Next resolves a path among pages: segment by
 * segment, a literal beats a param, which beats a catch-all. So
 * `/agents/manage` sorts before `/agents/[id]`.
 */
export function compareRouteSpecificity(a: string, b: string): number {
  const left = compilePattern(a).segments
  const right = compilePattern(b).segments
  for (let i = 0; i < Math.min(left.length, right.length); i++) {
    const diff = SEGMENT_RANK[left[i].kind] - SEGMENT_RANK[right[i].kind]
    if (diff !== 0) return diff
  }
  return right.length - left.length
}

/**
 * A route by its Next.js pattern, checked against the generated route list.
 *
 *   route("/hotels/[id]/rooms").href({ id: ref, query: { adults: 2 } })
 */
export function route<P extends AppRoute>(pattern: P): Route<P, QueryParams> {
  const { segments, matcher } = compilePattern(pattern)

  return {
    pattern,
    href(...args) {
      // The generic param shape is erased at runtime; read it as a plain record.
      const input: Record<string, unknown> & {
        query?: QueryParams | URLSearchParams
      } = args[0] ?? {}
      const parts: string[] = []
      for (const seg of segments) {
        if (seg.kind === "literal") {
          parts.push(seg.value)
          continue
        }
        const value = input[seg.name]
        if (seg.kind === "single") {
          if (typeof value !== "string" || value === "") {
            parts.push(
              missingParam(`routes: missing param "${seg.name}" for ${pattern}`)
            )
            continue
          }
          parts.push(encodeURIComponent(value))
          continue
        }
        const list = Array.isArray(value) ? value : []
        if (seg.kind === "catchAll" && list.length === 0) {
          parts.push(
            missingParam(
              `routes: missing catch-all "${seg.name}" for ${pattern}`
            )
          )
          continue
        }
        for (const v of list) parts.push(encodeURIComponent(String(v)))
      }
      return appendQuery("/" + parts.join("/"), input.query)
    },
    match(path) {
      const url = new URL(path, "http://routes.invalid")
      const m = matcher.exec(url.pathname)
      if (!m) return null
      const params: Record<string, string | string[]> = {}
      for (const seg of segments) {
        if (seg.kind === "literal") continue
        const raw = m.groups?.[seg.name]
        if (raw === undefined) continue
        params[seg.name] =
          seg.kind === "single"
            ? decodeSegment(raw)
            : raw.split("/").map(decodeSegment)
      }
      const query: Record<string, string> = {}
      url.searchParams.forEach((v, k) => {
        query[k] = v
      })
      // Same erasure as href(): the regex groups are exactly the pattern's params.
      return { ...params, query } as PathParams<P> & {
        readonly query: Record<string, string>
      }
    },
    test(path) {
      return matcher.test(new URL(path, "http://routes.invalid").pathname)
    },
    query() {
      return route(pattern)
    },
  }
}
